import React from 'react'
import { Badge, Card, CardContent, CardHeader } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useRewardsEnabled } from '../hooks/useRewardsEnabled'
import RewardsBingxConnectCard from '../components/RewardsBingxConnectCard'
import RewardsHowItWorksModal from '../components/RewardsHowItWorksModal'
import RewardsMemberTabs from '../components/RewardsMemberTabs'
import { fetchRewardsBingxUserDaily, type RewardsBingxDailyItem } from '../services/rewardsBingxService'
import { getRewardsAppSettings, listRewardsExchangeSettings } from '../services/rewardsSettingsService'
import { listRewardsLinksForUser, upsertRewardsLink } from '../services/rewardsLinksService'
import { getOpenRewardsCycle } from '../services/rewardsCyclesService'
import type { RewardsAccountExchangeLink, RewardsCycle } from '../types'

export default function RewardsPage() {
  const { add: toast } = useToast()
  const { user } = useAuth() as any
  const rewardsEnabled = useRewardsEnabled()

  const [loading, setLoading] = React.useState(true)
  const [accountId, setAccountId] = React.useState<string | null>(null)
  const [links, setLinks] = React.useState<RewardsAccountExchangeLink[]>([])
  const [cycle, setCycle] = React.useState<RewardsCycle | null>(null)
  const [multiplierX, setMultiplierX] = React.useState(3)
  const [usdtBrlRate, setUsdtBrlRate] = React.useState(0)
  const [bingxSignupUrl, setBingxSignupUrl] = React.useState<string | null>(null)
  const [bingxDaily, setBingxDaily] = React.useState<RewardsBingxDailyItem[]>([])
  const [bingxDailyLoading, setBingxDailyLoading] = React.useState(false)
  const [bingxDailyError, setBingxDailyError] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  const canLoad = !!user?.id && rewardsEnabled === true
  const bingxLink = React.useMemo(() => links.find((l) => l.exchange === 'bingx') || null, [links])
  const cycleForDisplay = cycle
  const bingxDailyIsDemo = false

  const load = React.useCallback(async () => {
    if (!user?.id) return
    setError(null)
    setLoading(true)
    try {
      const [settings, lnk, ex] = await Promise.all([
        getRewardsAppSettings().catch(() => null),
        listRewardsLinksForUser(user.id),
        listRewardsExchangeSettings().catch(() => []),
      ])

      if (settings) {
        setMultiplierX(Number(settings.multiplierX || 3))
        setUsdtBrlRate(Number(settings.usdtBrlRate || 0))
      }

      const bingx = (ex || []).find((r: any) => r.exchange === 'bingx') || null
      setBingxSignupUrl((bingx as any)?.signup_url || null)

      setAccountId(lnk.accountId)
      setLinks(lnk.links)

      if (lnk.accountId) {
        const c = await getOpenRewardsCycle(lnk.accountId)
        setCycle(c)
      } else {
        setCycle(null)
      }
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar Rewards')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  React.useEffect(() => {
    if (!canLoad) return
    load()
  }, [canLoad, load])

  const loadBingxDaily = React.useCallback(async () => {
    if (!canLoad) return
    if (!bingxLink || bingxLink.status !== 'linked') {
      setBingxDaily([])
      setBingxDailyError(null)
      return
    }

    setBingxDailyError(null)
    setBingxDailyLoading(true)
    try {
      const resp = await fetchRewardsBingxUserDaily({ budgetMs: 55_000 })
      if (!resp?.linked) {
        setBingxDaily([])
        return
      }
      setBingxDaily(resp.items || [])
      await load()
    } catch (e: any) {
      setBingxDailyError(e?.message || 'Falha ao buscar dados da BingX')
      setBingxDaily([])
    } finally {
      setBingxDailyLoading(false)
    }
  }, [bingxLink?.exchange_user_id, bingxLink?.status, canLoad, load])

  React.useEffect(() => {
    if (!canLoad) return
    if (bingxLink?.status !== 'linked') return
    loadBingxDaily()
  }, [canLoad, bingxLink?.status, bingxLink?.exchange_user_id, loadBingxDaily])

  async function onSubmitBingxUid(uid: string) {
    const link = await upsertRewardsLink({ exchange: 'bingx', exchangeUserId: uid })
    if (link.status === 'linked') {
      toast({ title: 'Rewards - BingX', description: 'Vinculado com sucesso.', variant: 'success', soft: true })
    } else if (link.status === 'not_linked') {
      toast({
        title: 'Rewards - BingX',
        description: 'UID não consta como indicado. Verifique se você se cadastrou pelo nosso link.',
        variant: 'warning',
        soft: true,
      })
    } else if (link.status === 'error') {
      toast({ title: 'Rewards - BingX', description: 'Falha ao validar UID na BingX.', variant: 'error' })
    } else {
      toast({ title: 'Rewards - BingX', description: 'Vínculo registrado. Verifique o status.', variant: 'info', soft: true })
    }
    await load()
  }

  if (rewardsEnabled === null) return <div className="p-6 text-sm text-foreground/70">Carregando...</div>
  if (rewardsEnabled === false) {
    return (
      <div className="mx-auto w-full max-w-[1100px] p-6">
        <Card>
          <CardHeader className="text-base font-semibold">Rewards</CardHeader>
          <CardContent className="text-sm text-foreground/70">
            O módulo Rewards não está habilitado para sua conta (role/plano). Em caso de dúvida, fale com o suporte.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Rewards</h1>
          <p className="text-sm text-foreground/70">Transforme seu volume na BingX em desconto progressivo na sua próxima fatura.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {bingxDailyIsDemo && (
            <Badge variant="soft" color="warning">
              DEMO
            </Badge>
          )}
          <RewardsHowItWorksModal />
        </div>
      </div>

      {error && <div className="mb-4 rounded-md bg-red-500/10 p-3 text-sm text-foreground">{error}</div>}

      <div className="space-y-6">
        <RewardsBingxConnectCard signupUrl={bingxSignupUrl} link={bingxLink} disabled={loading} onSubmitUid={onSubmitBingxUid} />

        <RewardsMemberTabs
          cycle={cycleForDisplay}
          multiplierX={multiplierX}
          usdtBrlRate={usdtBrlRate}
          links={links}
          bingxLink={bingxLink}
          bingxDaily={bingxDaily}
          bingxDailyLoading={bingxDailyLoading}
          bingxDailyError={bingxDailyError}
        />
      </div>
    </div>
  )
}
